import React, { useEffect, useState } from "react";
import {
  Users,
  CheckCircle,
  XCircle,
  Inbox,
  TrendingUp,
  ListChecks,
  SlidersHorizontal,
  MessageCircle,
  AlarmClockOff,
} from "lucide-react";
import {
  getDashboardSummary,
  getEngagementMetrics,
} from "../../apis/contactDashboard";

const RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const StatCard = ({ icon: Icon, label, value, sub, tone = "indigo" }) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    slate: "bg-slate-50 text-slate-700",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value ?? "—"}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone] || tones.indigo}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
};

const SourceBar = ({ sources }) => {
  const total = sources.reduce((s, x) => s + x.count, 0);
  if (total === 0) return <p className="text-sm text-gray-500">No data yet.</p>;
  const palette = {
    csv: "bg-indigo-500",
    manual: "bg-emerald-500",
    api: "bg-sky-500",
    broadcast: "bg-amber-500",
    inbound: "bg-rose-500",
    dynamic_segment: "bg-violet-500",
  };
  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
        {sources.map((s) => (
          <div
            key={s.source}
            className={palette[s.source] || "bg-gray-400"}
            style={{ width: `${(s.count / total) * 100}%` }}
            title={`${s.source}: ${s.count}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-3">
        {sources.map((s) => (
          <div key={s.source} className="flex items-center gap-2 text-xs">
            <span className={`w-2.5 h-2.5 rounded-full ${palette[s.source] || "bg-gray-400"}`} />
            <span className="text-gray-700 capitalize">{s.source.replace("_", " ")}</span>
            <span className="text-gray-400 ml-auto font-medium">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SparkBars = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-500">No data in range.</p>;
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d) => {
        const h = Math.max(2, (d.count / max) * 100);
        return (
          <div key={d.day} className="flex-1 group relative">
            <div
              className="bg-emerald-400 hover:bg-emerald-600 rounded-t transition-colors"
              style={{ height: `${h}%` }}
            />
            <span className="hidden group-hover:block absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] bg-gray-900 text-white px-1.5 py-0.5 rounded whitespace-nowrap">
              {d.count} on {d.day}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ContactsDashboardPage = ({ phoneId }) => {
  const [summary, setSummary] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phoneId) return;
    setLoading(true);
    setError("");
    Promise.all([
      getDashboardSummary({ phone_id: phoneId, range }),
      getEngagementMetrics({ phone_id: phoneId, range }),
    ])
      .then(([s, e]) => {
        setSummary(s.data);
        setEngagement(e.data);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Failed to load dashboard");
      })
      .finally(() => setLoading(false));
  }, [phoneId, range]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white"
        >
          {RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Contacts"
          value={loading ? "…" : summary?.totals.total.toLocaleString()}
          sub={`${summary?.totals.active ?? 0} active`}
          tone="indigo"
        />
        <StatCard
          icon={CheckCircle}
          label="Active"
          value={loading ? "…" : summary?.totals.active.toLocaleString()}
          tone="emerald"
        />
        <StatCard
          icon={XCircle}
          label="Inactive / Blocked"
          value={loading ? "…" : ((summary?.totals.inactive ?? 0) + (summary?.totals.blocked ?? 0)).toLocaleString()}
          tone="rose"
        />
        <StatCard
          icon={Inbox}
          label="With Unread"
          value={loading ? "…" : summary?.totals.with_unread.toLocaleString()}
          sub="across active conversations"
          tone="amber"
        />
        <StatCard
          icon={TrendingUp}
          label={`New (last ${summary?.growth?.range_days ?? 30}d)`}
          value={loading ? "…" : summary?.growth.in_range.toLocaleString()}
          sub={`${summary?.growth?.in_7d ?? 0} this week`}
          tone="sky"
        />
        <StatCard
          icon={ListChecks}
          label="Lists"
          value={loading ? "…" : summary?.lists.total.toLocaleString()}
          sub={`${summary?.lists.dynamic ?? 0} dynamic`}
          tone="indigo"
        />
        <StatCard
          icon={SlidersHorizontal}
          label="Custom Fields"
          value={loading ? "…" : summary?.custom_fields}
          tone="slate"
        />
        <StatCard
          icon={AlarmClockOff}
          label="Never Engaged"
          value={loading ? "…" : engagement?.never_engaged?.toLocaleString()}
          tone="slate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Where contacts come from</h3>
          </div>
          {loading || !summary ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <SourceBar sources={summary.sources || []} />
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Engagement</h3>
          </div>
          {loading || !engagement ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-2 text-sm">
              <Row icon={MessageCircle} label="Replied" value={engagement.replied_in_range} />
              <Row icon={CheckCircle} label="Read" value={engagement.read_in_range} />
              <Row icon={Inbox} label="Inbound" value={engagement.inbound_in_range} />
              <Row icon={TrendingUp} label="Outbound" value={engagement.outbound_in_range} />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Daily new contacts</h3>
          <span className="text-xs text-gray-500">{engagement?.range_days ?? 30}-day window</span>
        </div>
        {loading || !engagement ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <SparkBars data={engagement.daily_growth || []} />
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Top tags</h3>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (summary?.top_tags || []).length === 0 ? (
          <p className="text-sm text-gray-500">No tags yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {summary.top_tags.map((t) => (
              <span key={t.tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                {t.tag}
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-indigo-700 text-[10px]">{t.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2 text-gray-700">
      <Icon size={14} className="text-gray-400" />
      <span>{label}</span>
    </div>
    <span className="font-semibold text-gray-900">{(value || 0).toLocaleString()}</span>
  </div>
);

export default ContactsDashboardPage;
